/**
 * Historical IO / launch details, under the launched workspace's Activity log.
 *
 * The review grid's Instructions, Creative assignment and CTS findings cards
 * describe the plan AS REVIEWED before launch. On a launched campaign they move
 * here, labelled historical, and never annotate or restate current GT state
 * (Q4 / AC-07). Data comes from the reviewed draft's saved CTS snapshot
 * (GET /:id/gt-launch-history), read-only: no acknowledge, re-check, bulk fix
 * or creative upload control is wired up.
 */
import React, { useEffect, useState } from 'react';
import { api, getApiUrl } from '../../utils/apiConfig';
import { GtInstructionReviewPanel } from '../CampaignLaunchWorkflow/groundtruth/GtInstructionReviewPanel';
import { GtCreativeUploadPanel } from '../CampaignLaunchWorkflow/groundtruth/GtCreativeUploadPanel';
import { displayFinding } from '../CampaignLaunchWorkflow/groundtruth/gtInstructionLabels';
import { formatGtTimestamp } from './gtTime';
import '../CampaignLaunchWorkflow/CampaignLaunchWorkflow.css';
import './GtLaunchedWorkspace.css';

/** The CTS findings as the validator reported them: issues[] first, else the flat lists. */
export function historicalFindings(ctsReview) {
  if (!ctsReview) return [];
  if (Array.isArray(ctsReview.issues) && ctsReview.issues.length) {
    return ctsReview.issues.map((it) => ({
      severity: String(it.severity || 'warning').toLowerCase(),
      message: displayFinding(it.message || it.msg) || 'Validation issue',
      target: it.adgroup_name || it.ad_group_name || it.target || null,
    }));
  }
  const flat = (list, severity) => (Array.isArray(list) ? list : []).filter(Boolean)
    .map((m) => ({ severity, message: displayFinding(typeof m === 'string' ? m : m.message) || String(m), target: null }));
  return [...flat(ctsReview.errors, 'error'), ...flat(ctsReview.warnings, 'warning'), ...flat(ctsReview.infos, 'info')];
}

export default function GtLaunchHistory({ campaignId, accountId, userId }) {
  const [state, setState] = useState({ loading: true, history: null, error: null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(getApiUrl(`/api/v2/campaigns/${campaignId}/gt-launch-history`), {
          params: { accountId, userId },
        });
        if (!cancelled) setState({ loading: false, history: res.data || null, error: null });
      } catch (err) {
        if (!cancelled) setState({ loading: false, history: null, error: err?.response?.data?.error || err.message });
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId, accountId, userId]);

  const history = state.history;
  const ctsReview = history?.available ? history.ctsReview : null;
  const findings = historicalFindings(ctsReview);
  const sourceFile = history?.sourceFile;

  const downloadSource = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await api.get(getApiUrl('/api/upload/download-url'), {
        params: { gcsPath: sourceFile.gcsPath, accountId, userId },
      });
      if (!res.data?.url) throw new Error('No download URL returned');
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      setDownloadError(err?.response?.data?.error || err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="gtw-history clw" data-testid="gt-launch-history">
      <div className="gtw-history-head">
        <div>
          <div className="gtw-history-title">Historical IO / launch details</div>
          <div className="gtw-history-sub">
            As reviewed before launch{history?.savedAt ? ` (saved ${formatGtTimestamp(history.savedAt)})` : ''}.
            {' '}These are not current GroundTruth findings.
          </div>
        </div>
        {sourceFile?.gcsPath && (
          <button type="button" className="clw-btn clw-btn-secondary clw-btn-sm" onClick={downloadSource} disabled={downloading}
            title={downloadError ? `Download failed: ${downloadError}` : 'Download the IO file this campaign was launched from'}>
            {downloading ? 'Preparing…' : downloadError ? 'Retry download' : '⬇ Original IO file (historical)'}
          </button>
        )}
      </div>
      {state.loading && <div className="gtw-history-empty">Loading the pre-launch review…</div>}
      {state.error && <div className="gtw-history-empty">Could not load the pre-launch review: {state.error}</div>}
      {!state.loading && !state.error && !ctsReview && (
        <div className="gtw-history-empty">
          {history?.reason === 'no_review_snapshot'
            ? 'The reviewed draft has no saved review to show.'
            : 'No pre-launch review is linked to this launch.'}
        </div>
      )}
      {ctsReview && (
        <>
          <GtInstructionReviewPanel ctsReview={ctsReview} onAcknowledge={null} busy={false} />
          <GtCreativeUploadPanel ctsReview={ctsReview} upload={{}} onReload={null} />
          <div className="gtw-history-findings" data-testid="gt-history-findings">
            <div className="gtw-history-findings-title">
              CTS findings at review <span className="gtw-history-count">{findings.length}</span>
            </div>
            {findings.length === 0 ? (
              <div className="gtw-history-empty">The review reported no findings.</div>
            ) : (
              <ul>
                {findings.map((f, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i} className={`gtw-finding gtw-finding-${f.severity}`}>
                    <span className="gtw-finding-sev">{f.severity}</span>
                    {f.target && <span className="gtw-finding-target">{f.target}</span>}
                    <span>{f.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </section>
  );
}
